import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Lock, Wallet, ShoppingBag, Award, Shield, Coins, Landmark, Info } from "lucide-react";

// Economic Commons — dormant architecture surface.
// See /app/memory/brand_standards_v1.md §16
// This page NEVER exposes tokenomics, wallets, exchanges, staking, trading, or ICO features.

const MODULE_ICONS = {
  "Treasury": Landmark,
  "Marketplace": ShoppingBag,
  "Stewardship Ledger": Award,
  "Integrity Layer": Shield,
  "Digital Assets": Wallet,
  "New Earth Coin": Coins,
};

const DORMANCY_BADGE = (dormant) =>
  dormant
    ? { label: "Dormant · Reserved", cls: "bg-ink-soft/15 text-ink-soft" }
    : { label: "Active · Surface only", cls: "bg-forest/15 text-forest" };

export default function EconomicCommons() {
  const [items, setItems] = useState([]);
  const [signals, setSignals] = useState(null);

  useEffect(() => {
    api.get("/programs?department=funding")
      .then(({ data }) => setItems(data.filter(p => p?.metadata?.section === "economic_commons")))
      .catch(() => setItems([]));
    api.get("/observatory/signals")
      .then(({ data }) => setSignals(data))
      .catch(() => setSignals(null));
  }, []);

  const nec = useMemo(() => items.find(p => p.name === "New Earth Coin"), [items]);
  // Prefer LIVE signals from the Observatory. Fall back to reserved metadata only
  // if the Observatory endpoint is unreachable — never for lack of data.
  const activation = signals?.activation || nec?.metadata?.activation_requirements || {};
  const activationEntries = Object.entries(activation);
  const activationOverall = signals?.overall_activation_pct ?? (activationEntries.length
    ? Math.round(activationEntries.reduce((sum, [, v]) => sum + (v.current || 0), 0) / activationEntries.length)
    : 0);
  const signalsMet = signals?.signals_met ?? 0;
  const signalsTotal = signals?.signals_total ?? activationEntries.length;

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <div className="overline">Executive · Economic Commons</div>
        <h1 className="font-serif text-4xl mt-1">Economic Commons</h1>
        <p className="text-ink-soft mt-2 max-w-3xl leading-relaxed">
          Reserved architecture for the long-horizon economic layer of the institution. Currently dormant.
          No cryptocurrency exists. No token has been issued. No public investment is offered. This surface is
          admin-internal — never exposed on the public site.
        </p>
      </div>

      {/* Three-concept separator */}
      <section className="bg-admin-surface border border-line rounded-xl p-6" data-testid="ec-three-concepts">
        <div className="overline mb-4">Three separate concepts — do not confuse them</div>
        <div className="grid md:grid-cols-3 gap-6">
          <ConceptCard
            title="Money"
            status="Active today"
            statusCls="bg-forest text-bone"
            body="USD, COP, EUR. External world. Fiat currency, banking, Stripe donations. Handled by conventional accounting."
            testId="concept-money"
          />
          <ConceptCard
            title="Stewardship Credits"
            status="Future · internal"
            statusCls="bg-terracotta/15 text-terracotta"
            body="Non-tradable. Contribution-based. Records who has contributed what — restoration, teaching, research, mentorship. Not a currency."
            testId="concept-credits"
          />
          <ConceptCard
            title="New Earth Coin"
            status="Dormant"
            statusCls="bg-ink-soft/15 text-ink-soft"
            body="Working title. Reserved. Future transferable digital currency, anchored to proof of contribution — never speculation. Activates only when six institutional signals each cross 60%."
            testId="concept-nec"
          />
        </div>
      </section>

      {/* Modules grid */}
      <section data-testid="ec-modules">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="overline">Modules</div>
            <h2 className="font-serif text-2xl mt-1">Reserved components</h2>
          </div>
          <span className="text-xs text-ink-soft flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Placement: inside Funding department; conceptually witnessed by Observatory + Archives.</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(m => {
            const Icon = MODULE_ICONS[m.name] || Coins;
            const dormant = !!m.metadata?.dormant;
            const badge = DORMANCY_BADGE(dormant);
            return (
              <div key={m.id} className="bg-admin-surface border border-line rounded-xl p-5 flex flex-col gap-3" data-testid={`ec-module-${m.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-bone/60 border border-line flex items-center justify-center">
                      <Icon className="h-4 w-4 text-forest" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-serif text-lg text-ink leading-tight">{m.name}</div>
                      <Badge className={`${badge.cls} mt-1 text-[10px] uppercase tracking-widest`}>{badge.label}</Badge>
                    </div>
                  </div>
                  {dormant && <Lock className="h-4 w-4 text-ink-soft" />}
                </div>
                <p className="text-sm text-ink-soft leading-relaxed">{m.mission}</p>
                {m.metadata?.purpose && (
                  <p className="text-xs text-ink italic border-t border-line pt-3">{m.metadata.purpose}</p>
                )}
                {m.metadata?.blockchain_ready && (
                  <div className="text-[10px] uppercase tracking-widest text-terracotta">Blockchain-ready · anchoring only, no token</div>
                )}
              </div>
            );
          })}
          {!items.length && <div className="text-ink-soft text-sm col-span-full">No Economic Commons modules seeded yet.</div>}
        </div>
      </section>

      {/* New Earth Coin activation panel */}
      <section className="bg-forest text-bone rounded-xl overflow-hidden" data-testid="ec-nec-panel">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 text-bone/70 overline mb-2"><Coins className="h-3.5 w-3.5" strokeWidth={1.5} /> New Earth Coin — Activation Requirements</div>
              <h2 className="font-serif text-3xl leading-tight">Locked until the ecosystem is real.</h2>
              <p className="text-bone/70 mt-3 max-w-2xl text-sm leading-relaxed">
                A future contribution-based currency, anchored to real institutional activity — verified restoration, teaching, research, residencies.
                Never speculation. Six signals must each cross 60% before any token is considered.
              </p>
            </div>
            <div className="text-right">
              <div className="overline text-bone/60">Overall Economic Layer</div>
              <div className="font-serif text-5xl mt-1">{activationOverall}<span className="text-2xl text-bone/60">%</span></div>
              <Badge className="bg-terracotta text-bone mt-2 uppercase tracking-widest text-[10px]" data-testid="ec-nec-status">Locked</Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activationEntries.map(([key, v]) => {
              const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
              const pct = v.current || 0;
              const threshold = v.threshold || 60;
              const met = pct >= threshold;
              return (
                <div key={key} className="bg-bone/5 border border-bone/15 rounded-lg p-4" data-testid={`ec-signal-${key}`}>
                  <div className="flex justify-between items-baseline">
                    <div className="text-sm text-bone">{label}</div>
                    <div className="font-serif text-lg text-bone">
                      {pct}<span className="text-xs text-bone/60"> / {threshold}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-bone/15 rounded-full overflow-hidden mt-3">
                    <div
                      className={`h-full transition-all duration-700 ${met ? "bg-terracotta" : "bg-bone/60"}`}
                      style={{ width: `${Math.min(100, (pct / threshold) * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-bone/15 text-xs text-bone/60 leading-relaxed flex items-center justify-between gap-4 flex-wrap">
            <span>
              {signals
                ? <>Signals now read live from the Observatory · {signalsMet} of {signalsTotal} thresholds crossed · computed {new Date(signals.computed_at).toLocaleString()}</>
                : <>Signals will read from the Observatory once populated. This surface never speculates; it reflects.</>}
            </span>
            <span className="text-bone/50">The discipline is intentional: future stewards must see, at a glance, why the Economic Layer remains locked.</span>
          </div>
        </div>
      </section>

      {/* Governance boundary */}
      <section className="border border-dashed border-line rounded-xl p-6 bg-bone/30" data-testid="ec-boundary">
        <div className="overline text-terracotta mb-3">What NEOS will not do</div>
        <ul className="text-sm text-ink space-y-1.5 leading-relaxed">
          <li>· Launch a token before the ecosystem exists.</li>
          <li>· Present speculation as value.</li>
          <li>· Solicit public investment.</li>
          <li>· Build wallet, exchange, staking, or trading UI in the current stewardship phase.</li>
          <li>· Route the public through the Economic Commons page.</li>
        </ul>
      </section>
    </div>
  );
}

function ConceptCard({ title, status, statusCls, body, testId }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="font-serif text-xl text-ink">{title}</div>
      <Badge className={`${statusCls} text-[10px] uppercase tracking-widest`}>{status}</Badge>
      <p className="text-sm text-ink-soft leading-relaxed">{body}</p>
    </div>
  );
}
